import React from "react";
import { motion } from "framer-motion";

export const LogoIcon = ({ size = "md", className = "" }) => (
  <img
    src={`${process.env.PUBLIC_URL || ""}/progere-icon.png`}
    alt="PROGÈRE"
    className={`progere-icon progere-icon-${size} ${className}`}
  />
);

/**
 * Full branded marketing logo (hex P + "PROGÈRE DEVIS AI" text + tagline).
 * Use on landing hero, auth screens, billing receipts — anywhere with vertical space.
 */
export const LogoFull = ({ size = "md", className = "" }) => {
  const widths = { sm: 180, md: 280, lg: 380, xl: 520, "2xl": 640 };
  const w = widths[size] || widths.md;
  return (
    <img
      src={`${process.env.PUBLIC_URL || ""}/progere-logo-full.png`}
      alt="PROGÈRE Devis AI"
      className={className}
      style={{ width: w, height: "auto", maxWidth: "100%", display: "block" }}
    />
  );
};

/**
 * Compact brand mark for sidebar / navbar — uses the full marketing logo image.
 * Sizes: sm (sidebar mobile), md (sidebar desktop), lg (large headers).
 */
export const LogoBrand = ({ className = "", size = "md" }) => {
  const widths = { sm: 140, md: 190, lg: 240 };
  const w = widths[size] || widths.md;
  return (
    <div className={`inline-flex items-center select-none ${className}`}>
      <img
        src={`${process.env.PUBLIC_URL || ""}/progere-logo-full.png`}
        alt="PROGÈRE DEVIS AI"
        style={{ width: w, height: "auto", maxWidth: "100%", display: "block" }}
      />
    </div>
  );
};

/**
 * Ultra-compact logo for top nav bars — hex P icon + text stacked in 2 lines (PUNCH style).
 * Height ~40px so it never blows up the header.
 */
export const LogoCompact = ({ className = "" }) => (
  <div className={`inline-flex items-center gap-2.5 select-none ${className}`}>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: "rgba(192,0,0,0.15)", border: "1px solid rgba(192,0,0,0.35)" }}>
      <img
        src={`${process.env.PUBLIC_URL || ""}/progere-icon.png`}
        alt="PROGÈRE"
        style={{ width: 22, height: 22 }}
      />
    </div>
    <div className="leading-none">
      <div className="font-heading font-black text-[13px] tracking-tight">
        <span style={{ color: "#D4AF37" }}>PRO</span><span style={{ color: "#EF4444" }}>GÈRE</span>
      </div>
      <div className="font-heading font-black text-[13px] tracking-tight mt-0.5" style={{ color: "#F5F5F5" }}>
        DEVIS AI
      </div>
      <div className="text-[8px] font-bold tracking-[0.2em] uppercase mt-0.5" style={{ color: "rgba(212,175,55,0.6)" }}>
        MODULE PROGÈRE ERP
      </div>
    </div>
  </div>
);

export const Logo = ({ size = "md", showSub = true, showIcon = true, stack = false }) => {
  const sizes = {
    sm: { main: "text-lg", sub: "text-[9px]", icon: "sm", gap: "gap-2" },
    md: { main: "text-2xl", sub: "text-[10px]", icon: "md", gap: "gap-3" },
    lg: { main: "text-4xl sm:text-5xl", sub: "text-xs", icon: "lg", gap: "gap-4" },
    xl: { main: "text-5xl sm:text-7xl lg:text-8xl", sub: "text-sm", icon: "xl", gap: "gap-5" },
  }[size] || { main: "text-2xl", sub: "text-[10px]", icon: "md", gap: "gap-3" };

  if (stack) {
    return (
      <div className="inline-flex flex-col items-center select-none">
        {showIcon && <LogoIcon size={sizes.icon} className="mb-3" />}
        <div className="font-heading tracking-tight leading-none">
          <span className={`${sizes.main} font-black`}>
            <span style={{ color: "#D4AF37" }}>PRO</span><span style={{ color: "#EF4444" }}>GÈRE</span>
          </span>
        </div>
        {showSub && (
          <span className={`${sizes.sub} mt-2 font-medium tracking-[0.3em] uppercase`} style={{ color: "#F5F5F5" }}>
            Devis AI
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center ${sizes.gap} select-none`}>
      {showIcon && <LogoIcon size={sizes.icon} />}
      <div className="font-heading tracking-tight leading-none">
        <span className={`${sizes.main} font-black block`}>
          <span style={{ color: "#D4AF37" }}>PRO</span><span style={{ color: "#EF4444" }}>GÈRE</span>
        </span>
        {showSub && (
          <span className={`${sizes.sub} font-medium tracking-[0.25em] uppercase mt-1 block`} style={{ color: "#F5F5F5" }}>
            Devis AI
          </span>
        )}
      </div>
    </div>
  );
};

export const Badge = ({ status, label }) => (
  <span className={`badge badge-${status}`} data-testid={`badge-${status}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current" />
    {label}
  </span>
);

export const Spinner = () => (
  <motion.div
    className="inline-block w-4 h-4 rounded-full border-2 border-[#D4AF37] border-t-transparent"
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
  />
);

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex items-end justify-between flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
    <div className="min-w-0">
      <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight break-words" style={{ color: "#F5F5F5" }}>{title}</h1>
      {subtitle && <p className="text-xs sm:text-sm mt-2" style={{ color: "rgba(245,245,245,0.55)" }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const EmptyState = ({ icon, title, description, action }) => (
  <div className="glass-card p-12 flex flex-col items-center justify-center text-center">
    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(212,175,55,0.08)" }}>
      {icon}
    </div>
    <h3 className="font-heading text-xl font-semibold mb-2" style={{ color: "#F5F5F5" }}>{title}</h3>
    {description && <p className="text-sm mb-4" style={{ color: "rgba(245,245,245,0.55)" }}>{description}</p>}
    {action}
  </div>
);
